import React, { createContext, useContext, useMemo, useState, useEffect } from 'react'
import { api } from '../util/api'
import type { OrganizationResource } from '@/types/organizations'
import type { UserResource } from '@/types/users'
import { forgetMadeLinks } from '@/lib/hosts/madeLinks'
import { clearPendingInvite } from '@/lib/invites/pendingInvite'
import { getCable } from '@/lib/cable'

type User = UserResource

type Organization = OrganizationResource

type AuthContextValue = {
  token: string | null
  user: User | null
  organization: Organization | null
  isAuthenticated: boolean
  authReady: boolean
  login: (email: string, password: string) => Promise<void>
  completeOAuthLogin: (token: string) => void
  logout: () => void
  /** Replaces the signed-in user after a profile change, so every view shows it at once. */
  updateUser: (user: User) => void
  /** Loads the signed-in account again, as `/auth/me` has it now (after taking or leaving an event as its manager,
   *  which changes where the account may go). Resolves to it once it is in place, or to null when it couldn't load (the
   *  account stays as it was) or isn't this session's any more. */
  refreshUser: () => Promise<{ user: User; organization: Organization | null } | null>
  switchOrganization: (orgId: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('sb_token'))
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [authReady, setAuthReady] = useState(() => !localStorage.getItem('sb_token'))

  useEffect(() => {
    // The socket connects as whoever is signed in: open subscriptions move to the new token's connection.
    getCable()
    if (!token) {
      api.setToken(null)
      setUser(null)
      setOrganization(null)
      setAuthReady(true)
      return
    }

    let cancelled = false
    setAuthReady(false)
    api.setToken(token)

    api
      .get<{ data: { user: User; organization?: Organization } }>('/auth/me')
      .then((res) => {
        if (cancelled) return
        setUser(res.data.user)
        setOrganization(res.data.organization || null)
      })
      .catch(() => {
        if (cancelled) return
        // Invalid token; clear
        setToken(null)
        setUser(null)
        setOrganization(null)
        localStorage.removeItem('sb_token')
      })
      .finally(() => {
        if (!cancelled) setAuthReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [token])

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      organization,
      isAuthenticated: !!token,
      authReady,
      async login(email: string, password: string) {
        const res = await api.post<{ data: { token: string; user: User; organization?: Organization } }>(
          '/auth/login',
          { user: { email, password } }
        )
        const payload = (res as any)?.data?.data ?? (res as any)?.data ?? res
        const newToken = payload.token
        setToken(newToken)
        localStorage.setItem('sb_token', newToken)
        api.setToken(newToken)
        setUser(payload.user)
        setOrganization(payload.organization || null)
        setAuthReady(true)
      },
      completeOAuthLogin(newToken: string) {
        setAuthReady(false)
        setToken(newToken)
        localStorage.setItem('sb_token', newToken)
        api.setToken(newToken)
      },
      logout() {
        // A pending invite (/invite) and the manager links this tab made are this person's: the next to sign in on
        // this device mustn't find them.
        clearPendingInvite()
        forgetMadeLinks()
        setToken(null)
        setUser(null)
        setOrganization(null)
        setAuthReady(true)
        localStorage.removeItem('sb_token')
        api.setToken(null)
      },
      updateUser(nextUser: User) {
        setUser(nextUser)
      },
      async refreshUser() {
        if (!token) return null
        try {
          const res = await api.get<{ data: { user: User; organization?: Organization } }>('/auth/me')
          // Signed out or another account meanwhile: this answer is no longer theirs.
          if (localStorage.getItem('sb_token') !== token) return null
          const account = { user: res.data.user, organization: res.data.organization || null }
          setUser(account.user)
          setOrganization(account.organization)
          return account
        } catch {
          // The account stays as loaded; the next page load reads it afresh.
          return null
        }
      },
      async switchOrganization(orgId: string) {
        const res = await api.post<{ token: string; organization: Organization }>(`/organizations/${orgId}/switch`)
        const payload = (res as any)?.data ?? res
        const newToken = payload.token
        localStorage.setItem('sb_token', newToken)
        api.setToken(newToken)
        // Full reload so every page re-fetches with the new org context
        window.location.reload()
      },
    }),
    [token, user, organization, authReady]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
